import AppConfig from 'config/AppConfig'
import useSession, { useStore } from 'hooks/useSession'
import { RouterInputs, trpc } from 'api'
import { useCallback, useMemo } from 'react'
import { debugAvatar } from '@order/utils/getDebugLog'
import { create } from 'zustand'
import { Refraction } from '@ca/common'

type AvatarContext = RouterInputs['avatar']['initialize'] | null

type AvatarSessionState = {
  context: AvatarContext
  sessionId: string | null
  update: (sessionId: string, value: AvatarContext) => void
  reset: () => void
}

export const useAvatarSessionState = create<AvatarSessionState>((set) => ({
  context: null,
  sessionId: null,
  update: (sessionId, value) => set({ sessionId, context: value }),
  reset: () => set({ context: null, sessionId: null }),
}))

export const useAvatarSessionContext = () => {
  const store = useStore()
  const serialNumber = AppConfig.serialNumber

  const context = useMemo(() => {
    if (!store?.ref) {
      return undefined
    }
    return {
      storeId: store.ref,
      serialNumber,
    }
  }, [store, serialNumber])

  return context
}

export const useAvatarSessionEnd = () => {
  const reset = useAvatarSessionState((state) => state.reset)
  const sessionId = useSession((state) => state.sessionId)

  const endMutation = trpc.avatar.end.useMutation({
    onSuccess: (data, vars) => {
      debugAvatar.log('endMutation.onSuccess', data, vars)
      reset()
    },
    onError: (error, vars) => {
      console.error('endMutation.onError', error, vars)
    },
  })

  const currentContext = useAvatarSessionState((state) => state.context)
  const currentSessionId = useAvatarSessionState((state) => state.sessionId)

  const end = useCallback(() => {
    // If the session is not initialized, or the session ID is not the same, return
    if (
      !currentContext ||
      !currentSessionId ||
      currentSessionId !== sessionId
    ) {
      return
    }
    endMutation.mutateAsync(currentContext)
  }, [endMutation, currentContext, currentSessionId, sessionId])

  return {
    end,
    mutation: endMutation,
  }
}

export const useAvatarSessionInitialize = () => {
  const store = useStore()
  const serialNumber = AppConfig.serialNumber
  const updateSession = useAvatarSessionState((state) => state.update)
  const currentSessionId = useAvatarSessionState((state) => state.sessionId)
  const sessionId = useSession((state) => state.sessionId)

  const initMutation = trpc.avatar.initialize.useMutation({
    onSuccess: (data, vars) => {
      if (!data) {
        console.warn('Session data is required')
        return
      }
      debugAvatar.log('initializeMutation.onSuccess', data, vars)

      if (!sessionId) {
        console.warn('Session ID is required')
        return
      }
      updateSession(sessionId, vars)
    },
    onError: (error, vars) => {
      console.error('initializeMutation.onError', error, vars)
    },
  })

  const isPending = initMutation.isPending
  const mutateAsync = initMutation.mutateAsync

  const initialize = useCallback(
    (refraction: Refraction) => {
      if (!store?.ref) {
        console.warn('Store ID is required')
        return
      }
      if (isPending) {
        console.warn('Session already initializing, skipping')
        return
      }
      if (currentSessionId && currentSessionId === sessionId) {
        console.warn('Session already initialized, skipping')
        return
      }
      mutateAsync({
        storeId: store.ref,
        serialNumber,
        refraction,
      })
    },
    [store, serialNumber, isPending, mutateAsync, currentSessionId, sessionId]
  )

  return {
    initialize,
    mutation: initMutation,
  }
}

/**
 * Start/stop session
 */
export const useAvatarSession = () => {
  const { end } = useAvatarSessionEnd()
  const { initialize } = useAvatarSessionInitialize()

  return {
    initialize,
    end,
  }
}
